import { useEffect, useSyncExternalStore } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useMatch } from 'react-router-dom';
import {
  BASE_BRAND,
  contrastText,
  type BrandDto,
  type EffectiveBrandDto,
  type SkinCssDto,
} from '@fyr/shared';
import { api } from './api';

/**
 * §17 runtime theming.
 *   • Mode: system | light | dark (per viewer, localStorage) → <html data-theme>. theme-init.js applies it before paint.
 *   • Brand: the ACTIVE tenant's brand, overridden by the current project's — fetched per tenant/project and applied as
 *     CSS variables (+ favicon, title). The org switcher changes the tenant, so the brand follows. No rebuild.
 *   • Skin (§23 M12.6): the organisation's theme pack, compiled by the api into one stylesheet (<style id="fyr-skin">)
 *     and cached in localStorage so theme-init.js applies it before the first paint next time.
 *   • Precedence: base tokens (theme.css) → skin (more specific selectors) → brand (inline on <html>).
 *   • Canvas consumers (the Glide grid) can't read CSS variables, so token() resolves them to concrete colours and
 *     useThemeVersion() re-renders them when the mode, skin or brand changes.
 */
export type ThemeMode = 'system' | 'light' | 'dark';
const KEY = 'fyr.theme';
const media = () => matchMedia('(prefers-color-scheme: dark)');

// ── change notifications (mode or brand) ──────────────────────────────────────
let version = 0;
const listeners = new Set<() => void>();
function changed() {
  version++;
  resolved.clear();
  for (const l of listeners) l();
}
const subscribe = (l: () => void) => (listeners.add(l), () => listeners.delete(l));
/** Bumps whenever colours change (mode, skin or brand) — dependency for canvas themes. */
export const useThemeVersion = () => useSyncExternalStore(subscribe, () => version);

// ── mode ───────────────────────────────────────────────────────────────────────
function storedMode(): ThemeMode {
  try {
    const v = localStorage.getItem(KEY);
    return v === 'light' || v === 'dark' ? v : 'system';
  } catch {
    return 'system';
  }
}
function applyMode(mode: ThemeMode) {
  const dark = mode === 'dark' || (mode === 'system' && media().matches);
  if (document.documentElement.dataset.theme !== (dark ? 'dark' : 'light')) {
    document.documentElement.dataset.theme = dark ? 'dark' : 'light';
    changed();
  }
}
// One mode for the whole app (the toggle and the runtime share it).
let currentMode: ThemeMode = typeof window === 'undefined' ? 'system' : storedMode();
export function setThemeMode(m: ThemeMode) {
  try {
    localStorage.setItem(KEY, m);
  } catch {
    /* per-viewer convenience only */
  }
  currentMode = m;
  applyMode(m);
  changed();
}
export const useThemeMode = () => useSyncExternalStore(subscribe, () => currentMode);

/** Mounted once at the app root: applies the mode and follows the OS while the choice is "system". */
export function useThemeRuntime() {
  useEffect(() => {
    applyMode(currentMode);
    const m = media();
    const onChange = () => currentMode === 'system' && applyMode('system');
    m.addEventListener('change', onChange);
    return () => m.removeEventListener('change', onChange);
  }, []);
}

// ── tokens for canvas ─────────────────────────────────────────────────────────
/** A token's raw value (a font stack, a length…), as the skin and base CSS resolve it right now. */
export const cssVar = (name: `--${string}`) =>
  getComputedStyle(document.documentElement).getPropertyValue(name).trim();
const resolved = new Map<string, string>();
let probe: CanvasRenderingContext2D | null = null;
/** A design token as a concrete #rrggbb (resolves var() and color-mix() via the browser's own colour parser). */
export function token(name: `--${string}`): string {
  const hit = resolved.get(name);
  if (hit) return hit;
  const raw = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  probe ??= Object.assign(document.createElement('canvas'), { width: 1, height: 1 }).getContext('2d', {
    willReadFrequently: true,
  });
  let out = raw || '#000000';
  if (probe) {
    probe.clearRect(0, 0, 1, 1);
    probe.fillStyle = '#000000';
    probe.fillStyle = raw;
    probe.fillRect(0, 0, 1, 1);
    const [r, g, b] = probe.getImageData(0, 0, 1, 1).data;
    out = `#${[r, g, b].map((v) => v!.toString(16).padStart(2, '0')).join('')}`;
  }
  resolved.set(name, out);
  return out;
}

// ── brand ─────────────────────────────────────────────────────────────────────
const assetUrl = (id: string) => `/api/brand/assets/${id}`;
function setFavicon(href: string) {
  let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (!link) {
    link = Object.assign(document.createElement('link'), { rel: 'icon' });
    document.head.append(link);
  }
  if (link.href !== new URL(href, location.href).href) {
    link.href = href;
    link.type = href.endsWith('.svg') ? 'image/svg+xml' : 'image/png';
  }
}
export function applyBrand(
  b:
    | (Pick<EffectiveBrandDto, 'primary' | 'accent' | 'faviconAssetId'> &
        Partial<Pick<EffectiveBrandDto, 'from'>>)
    | null,
) {
  const root = document.documentElement.style;
  const before = root.getPropertyValue('--brand');
  // Only a REAL brand goes inline (and so wins); with none, the skin's or the base colour shows through.
  const branded = !!b && b.from?.primary !== 'base';
  const primary = branded ? b.primary : null;
  if (primary) {
    root.setProperty('--brand', primary);
    root.setProperty('--brand-contrast', contrastText(primary));
  } else {
    root.removeProperty('--brand');
    root.removeProperty('--brand-contrast');
  }
  if (b && b.accent !== b.primary) root.setProperty('--accent', b.accent);
  else root.removeProperty('--accent');
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', primary ?? BASE_BRAND.primary);
  setFavicon(b?.faviconAssetId ? assetUrl(b.faviconAssetId) : '/favicon.svg');
  if (before !== (primary ?? '')) changed();
}

/** The brand for what's on screen: active tenant (+ current project). Re-fetches when either changes. */
export function useBrand(tenantId: string | undefined) {
  const projectId = useMatch('/p/:projectId/*')?.params.projectId;
  const q = useQuery({
    queryKey: ['brand', tenantId, projectId ?? null],
    queryFn: () => api.get<BrandDto>(`/brand${projectId ? `?projectId=${projectId}` : ''}`),
    enabled: !!tenantId,
    staleTime: 60_000,
  });
  useEffect(() => {
    if (!tenantId) applyBrand(null);
    else if (q.data) applyBrand(q.data.effective);
  }, [tenantId, q.data]);
  return {
    brand: q.data ?? null,
    logoUrl: q.data?.effective.logoAssetId ? assetUrl(q.data.effective.logoAssetId) : null,
  };
}

export const brandAssetUrl = assetUrl;

// ── skin (§23 M12.6) ──────────────────────────────────────────────────────────
const SKIN_KEY = 'fyr.skin';
/** Put the skin's stylesheet in place (or remove it) and remember it for the next first paint. */
export function applySkin(s: SkinCssDto | null) {
  let el = document.getElementById('fyr-skin') as HTMLStyleElement | null;
  const css = s?.css ?? '';
  if (!el && css) el = Object.assign(document.createElement('style'), { id: 'fyr-skin' });
  if (el) {
    // labelled every time (theme-init may already have put the same CSS in place from the cache)
    el.dataset.skin = s ? `${s.id}@${s.version}:${s.density}` : '';
    if (el.textContent !== css) {
      el.textContent = css;
      changed();
    }
    // Last in <head>, after the app's own styles (its selectors win anyway; this keeps equal ones in order too).
    if (el.parentNode !== document.head || el.nextElementSibling) document.head.append(el);
  }
  try {
    if (s?.css)
      localStorage.setItem(
        SKIN_KEY,
        JSON.stringify({ id: s.id, version: s.version, density: s.density, css: s.css }),
      );
    else localStorage.removeItem(SKIN_KEY);
  } catch {
    /* the cache only saves a flash */
  }
}

/** The skin for whoever is signed in (or the platform default before sign-in). Re-checked every 2 minutes and on focus. */
export function useSkin(tenantId: string | undefined) {
  const q = useQuery({
    queryKey: ['skin', tenantId ?? null],
    // {} when there's no skin at all (base look)
    queryFn: () => api.get<Partial<SkinCssDto>>('/skin'),
    // §23 M12.8 a new default or a new version reaches open tabs within minutes (cheap: ~3 KB, compiled once)
    staleTime: 60_000,
    refetchInterval: 2 * 60_000,
    refetchOnWindowFocus: true,
  });
  useEffect(() => {
    if (q.data) applySkin(q.data.css !== undefined ? (q.data as SkinCssDto) : null);
  }, [q.data]);
}
